import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

interface AgentOption {
  id: number;
  name: string;
  company_name?: string;
  has_avatar: boolean;
}

@customElement('cp-agent-mention-dropdown')
export class CpAgentMentionDropdown extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Boolean }) open = false;
  @property({ type: String }) query = '';
  @state() private _agents: AgentOption[] = [];
  @state() private _filteredAgents: AgentOption[] = [];
  @state() private _loading = false;
  @state() private _selectedIndex = 0;

  private _loaded = false;
  private _touchStartY = 0;

  updated(changed: Map<string, unknown>): void {
    if (changed.has('open') && this.open && !this._loaded) {
      void this._loadAgents();
    }
    if (changed.has('query') || changed.has('_agents')) {
      this._filterAgents();
    }
    if (changed.has('open') && this.open) {
      this._selectedIndex = 0;
    }
  }

  private async _loadAgents(): Promise<void> {
    this._loading = true;
    try {
      const res = await fetch('/api/agents?scope=all');
      if (!res.ok) throw new Error('Failed to load agents');
      const agents = await res.json() as Array<{ id: number; name: string; company_id?: number; has_avatar?: boolean }>;

      // Load company names for context
      const companyRes = await fetch('/api/companies');
      const companies = companyRes.ok
        ? await companyRes.json() as Array<{ id: number; name: string }>
        : [];
      const companyMap = new Map(companies.map((c: { id: number; name: string }) => [c.id, c.name]));

      this._agents = agents.map(a => ({
        id: a.id,
        name: a.name,
        company_name: a.company_id ? companyMap.get(a.company_id) : undefined,
        has_avatar: !!a.has_avatar,
      }));
      this._loaded = true;
    } catch {
      this._agents = [];
    }
    this._loading = false;
  }

  private _filterAgents(): void {
    if (!this.query) {
      this._filteredAgents = this._agents.slice(0, 20);
      this._selectedIndex = 0;
      return;
    }
    const q = this.query.toLowerCase();
    this._filteredAgents = this._agents
      .filter(a => a.name.toLowerCase().includes(q) || (a.company_name?.toLowerCase().includes(q) ?? false))
      .slice(0, 20);
    this._selectedIndex = 0;
  }

  handleKeydown(e: KeyboardEvent): boolean {
    if (!this.open) return false;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this._selectedIndex = Math.min(this._selectedIndex + 1, this._filteredAgents.length - 1);
      this._scrollActiveIntoView();
      return true;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      this._selectedIndex = Math.max(this._selectedIndex - 1, 0);
      this._scrollActiveIntoView();
      return true;
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      if (this._filteredAgents.length > 0) {
        e.preventDefault();
        this._selectAgent(this._filteredAgents[this._selectedIndex]);
        return true;
      }
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      this.dispatchEvent(new CustomEvent('mention-dismissed', { bubbles: true, composed: true }));
      return true;
    }
    return false;
  }

  private _scrollActiveIntoView(): void {
    this.updateComplete.then(() => {
      const active = this.querySelector('.cp-agent-mention-dropdown__item--active');
      active?.scrollIntoView({ block: 'nearest' });
    });
  }

  private _selectAgent(agent: AgentOption): void {
    this.dispatchEvent(new CustomEvent('agent-selected', {
      bubbles: true,
      composed: true,
      detail: { agentId: agent.id, agentName: agent.name },
    }));
  }

  render() {
    if (!this.open) return nothing;

    if (this._loading) {
      return html`
        <div class="cp-agent-mention-dropdown">
          <div class="cp-agent-mention-dropdown__empty">Lade Agents...</div>
        </div>
      `;
    }

    if (this._filteredAgents.length === 0) {
      return html`
        <div class="cp-agent-mention-dropdown">
          <div class="cp-agent-mention-dropdown__empty">Keine Agents gefunden</div>
        </div>
      `;
    }

    return html`
      <div class="cp-agent-mention-dropdown">
        ${this._filteredAgents.map((a, i) => html`
          <button
            class="cp-agent-mention-dropdown__item ${i === this._selectedIndex ? 'cp-agent-mention-dropdown__item--active' : ''}"
            @mousedown=${(e: Event) => { e.preventDefault(); this._selectAgent(a); }}
            @touchstart=${(e: TouchEvent) => { this._touchStartY = e.touches[0].clientY; }}
            @touchend=${(e: TouchEvent) => { const dy = Math.abs(e.changedTouches[0].clientY - this._touchStartY); if (dy < 10) { e.preventDefault(); this._selectAgent(a); } }}
          >
            ${a.has_avatar
              ? html`<img class="cp-agent-mention-dropdown__avatar" src="/api/agents/${a.id}/avatar" alt="" />`
              : html`<span class="cp-agent-mention-dropdown__avatar cp-agent-mention-dropdown__avatar--placeholder">${a.name.charAt(0)}</span>`
            }
            <span class="cp-agent-mention-dropdown__name">${a.name}</span>
            ${a.company_name ? html`<span class="cp-agent-mention-dropdown__company">${a.company_name}</span>` : nothing}
          </button>
        `)}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-agent-mention-dropdown': CpAgentMentionDropdown;
  }
}
